import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, FormField, Input, Modal, Select, useToast } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { rupeeInputToPaise } from '@/utils/price-input';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import type { LocationStock, StockAdjustmentType } from '@/types/product';

const ADJUSTMENT_TYPE_OPTIONS: { label: string; value: StockAdjustmentType }[] = [
  { label: 'Purchase (Stock In)', value: 'PURCHASE' },
  { label: 'Manual Adjustment', value: 'ADJUSTMENT' },
  { label: 'Opening Stock', value: 'OPENING_STOCK' },
  { label: 'Damage Write-off', value: 'DAMAGE' },
  { label: 'Wastage', value: 'WASTAGE' },
  { label: 'Expiry Write-off', value: 'EXPIRY_WRITE_OFF' },
];

/**
 * Adjust the stock of one sellable row — a variant, from the parent's Stock tab
 * or the variant's own page (POS comments item 15: variants had no way to
 * change stock). Same form as the product page's Adjust Stock.
 *
 * Products are location-scoped rows sharing a SKU: `productId` may be any
 * location's row and `location_id` picks the one adjusted, so the location is
 * asked for whenever the SKU is stocked at more than one.
 */
export function StockAdjustModal({
  productId,
  title,
  stockRows,
  defaultLocationId,
  onClose,
  onAdjusted,
}: {
  productId: string;
  title: string;
  stockRows: LocationStock[];
  defaultLocationId?: string;
  onClose: () => void;
  onAdjusted: () => void;
}) {
  const currency = useTenantCurrency();
  const { toast } = useToast();
  const [type, setType] = useState<StockAdjustmentType | ''>('');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');
  const [batchId, setBatchId] = useState('');
  const [locationId, setLocationId] = useState(defaultLocationId ?? '');
  const [error, setError] = useState('');
  const isWriteOff = type === 'DAMAGE' || type === 'WASTAGE' || type === 'EXPIRY_WRITE_OFF';
  const needsLocation = stockRows.length > 1;

  const { data: openBatches } = useQuery({
    queryKey: ['product', productId, 'batches'],
    queryFn: () => productService.getBatches(productId),
    enabled: isWriteOff,
  });

  const adjust = useMutation({
    mutationFn: () =>
      productService.adjustStock(productId, {
        type: type as StockAdjustmentType,
        quantity: Number(qty),
        ...(type === 'PURCHASE' && unitCost ? { unit_cost: rupeeInputToPaise(unitCost, currency) ?? 0 } : {}),
        ...(notes ? { notes } : {}),
        ...(isWriteOff && batchId ? { batch_id: batchId } : {}),
        ...(locationId ? { location_id: locationId } : {}),
      }),
    onSuccess: (res) => {
      // QA IS4-002: above the wastage limit the write-off waits in Approvals.
      if (res.pending_approval) {
        toast({ title: 'Sent for approval — above your wastage limit. The stock is held until it is approved.', variant: 'warning' });
      }
      onAdjusted();
    },
    // e.g. 400 "Insufficient stock" on a write-off — keep the dialog open and say why.
    onError: (err: Error) => setError(err.message || 'Failed to adjust stock'),
  });

  return (
    <Modal open title={title} onClose={onClose}>
      <div className="space-y-4 p-4">
        {error && (
          <p role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/30 dark:text-danger-300">
            {error}
          </p>
        )}
        {needsLocation && (
          <FormField label="Location" required>
            <Select
              options={stockRows.map((s) => ({
                label: `${s.location_name} — ${s.stock_quantity} in stock`,
                value: s.location_id,
              }))}
              value={locationId}
              onChange={(v) => setLocationId(typeof v === 'string' ? v : '')}
              placeholder="Select location"
            />
          </FormField>
        )}
        <FormField label="Adjustment Type" required>
          <Select
            options={ADJUSTMENT_TYPE_OPTIONS}
            value={type}
            onChange={(v) => setType(typeof v === 'string' ? (v as StockAdjustmentType) : '')}
            placeholder="Select adjustment type"
          />
        </FormField>
        <FormField label="Quantity" required>
          <Input type="number" value={qty} onChange={setQty} placeholder="e.g. 10" />
        </FormField>
        {type === 'PURCHASE' && (
          <FormField label={`Unit Cost (${getCurrencySymbol(currency)})`} required>
            <Input type="number" variant="currency" currency={currency} value={unitCost} onChange={setUnitCost} placeholder="e.g. 350" />
          </FormField>
        )}
        {isWriteOff && (openBatches?.length ?? 0) > 0 && (
          <FormField label="Batch" helperText="Leave blank to write off the soonest-expiring batches first (FEFO).">
            <Select
              options={[
                { label: 'Auto — soonest expiry first', value: '' },
                ...(openBatches ?? []).map((b) => ({
                  label: `${b.batch_number} · exp ${b.expiry_date} · ${b.quantity} in stock`,
                  value: b.id,
                })),
              ]}
              value={batchId}
              onChange={(v) => setBatchId(typeof v === 'string' ? v : '')}
              placeholder="Auto — soonest expiry first"
            />
          </FormField>
        )}
        <FormField label="Notes">
          <Input value={notes} onChange={setNotes} placeholder="Optional notes" />
        </FormField>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => { setError(''); adjust.mutate(); }}
            disabled={
              !type || !qty ||
              (needsLocation && !locationId) ||
              (type === 'PURCHASE' && !unitCost) ||
              adjust.isPending
            }
          >
            {adjust.isPending ? 'Adjusting…' : 'Adjust Stock'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
